import { Suspense, type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

import { PanelLink } from './PanelLink.js';
import { QueryError } from './QueryError.js';
import { EmptyState, Panel, PanelHeader, Spinner } from './primitives.js';

/**
 * The chrome around a chart, and the accessible equivalent underneath it.
 *
 * A chart has five states a panel does not — loading, refetching, failed,
 * disabled (the filter is invalid so nothing was requested), and empty — and
 * getting them wrong is what makes a dashboard feel broken. Spinners that never
 * resolve, a card that collapses to nothing on refetch, a red error box that
 * takes the whole page down. They are handled once, here.
 *
 * THE TABLE TWIN IS NOT OPTIONAL. Every chart ships an `sr-only` table carrying
 * the same numbers. A canvas or SVG of positioned rectangles is not readable by
 * assistive technology no matter how many aria labels are hung on it, and this
 * is a screen people are evaluated against.
 *
 * `height` reserves space BEFORE the chart loads, including the axis band. A
 * container that grows when the data arrives shifts everything below it, and on
 * this page that is six other panels.
 */
export function ChartFrame({
  title,
  description,
  icon: Icon,
  to,
  height = 260,
  isLoading,
  isFetching,
  isError,
  error,
  onRetry,
  disabled,
  disabledMessage = 'Perbaiki rentang tanggal di atas untuk melihat data.',
  isEmpty,
  empty,
  table,
  children,
}: {
  title: string;
  description?: string;
  icon?: LucideIcon;
  to?: string;
  height?: number;
  isLoading?: boolean;
  isFetching?: boolean;
  isError?: boolean;
  /**
   * The rejection itself, so the panel can say WHY rather than only that it
   * failed. Optional for the callers that only have `isError` to hand; without
   * it this degrades to the sentence it used to show alone.
   */
  error?: unknown;
  onRetry?: () => void;
  disabled?: boolean;
  disabledMessage?: string;
  isEmpty?: boolean;
  empty: ReactNode;
  /** The accessible equivalent. Rendered `sr-only`; never omit it. */
  table: { caption: string; head: string[]; rows: string[][] };
  children: ReactNode;
}) {
  const header = (
    <PanelHeader
      title={title}
      {...(description ? { description } : {})}
      {...(to ? { action: <PanelLink to={to} /> } : {})}
    />
  );

  return (
    <Panel className="flex h-full flex-col">
      <div className="flex items-start gap-2.5">
        {Icon ? <Icon size={18} className="mt-1 shrink-0 text-steel" aria-hidden /> : null}
        <div className="min-w-0 flex-1">{header}</div>
      </div>

      <div className="min-w-0 flex-1" style={{ minHeight: height }}>
        {disabled ? (
          <EmptyState title="Rentang tanggal belum valid" description={disabledMessage} />
        ) : isError ? (
          <div className="p-2">
            <QueryError
              what={title.toLowerCase()}
              error={error}
              {...(onRetry ? { onRetry } : {})}
            />
          </div>
        ) : isLoading ? (
          <div className="flex h-full items-center justify-center">
            <Spinner label="Memuat grafik…" />
          </div>
        ) : isEmpty ? (
          <>{empty}</>
        ) : (
          <>
            {/*
              Held at reduced opacity on refetch rather than replaced by a
              skeleton: changing the date should not blank six panels and jump
              the layout. The numbers stay readable while the new ones arrive.
            */}
            <div
              className={`min-w-0 transition-opacity duration-150 ${isFetching ? 'opacity-60' : ''}`}
              style={{ height }}
            >
              <Suspense
                fallback={
                  <div className="flex h-full items-center justify-center">
                    <Spinner label="Memuat grafik…" />
                  </div>
                }
              >
                {children}
              </Suspense>
            </div>

            <table className="sr-only">
              <caption>{table.caption}</caption>
              <thead>
                <tr>
                  {table.head.map((cell) => (
                    <th key={cell} scope="col">
                      {cell}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {table.rows.map((row, index) => (
                  <tr key={`${row[0] ?? ''}-${index}`}>
                    {row.map((cell, cellIndex) => (
                      <td key={`${cell}-${cellIndex}`}>{cell}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </>
        )}
      </div>
    </Panel>
  );
}
